import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Input,
  message,
  Button,
  Card,
  Empty,
  Spin,
} from 'antd';
import { WalletOutlined } from '@ant-design/icons';
import {
  SourceWallet,
  incomeService,
  formatCurrency,
} from '../../services/incomeService';
import styles from '../common/DrawerModalMobile.module.css';

interface SellectWalletModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (wallet: SourceWallet) => void;
  selectedWalletId?: number;
}

export const SellectWalletModalMobile: React.FC<SellectWalletModalMobileProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedWalletId,
}) => {
  const [wallets, setWallets] = useState<SourceWallet[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadWallets();
    }
  }, [isOpen]);

  const loadWallets = async () => {
    try {
      setLoading(true);
      const walletsData = await incomeService.getWallets();
      setWallets(walletsData.filter(wallet => wallet.is_active));
    } catch (error) {
      message.error('Không thể tải danh sách ví');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletSelect = (wallet: SourceWallet) => {
    onSelect(wallet);
    onClose();
  };

  const filteredWallets = wallets;

  const getWalletDisplayName = (wallet: SourceWallet) => {
    return `${wallet.name} (${wallet.source_name})`;
  };

  const getWalletBalanceDisplay = (wallet: SourceWallet) => {
    return `${formatCurrency(wallet.current_balance)}`;
  };

  const getAtmCardColorClass = (wallet: SourceWallet) => {
    const color = wallet.source_color || '#FF89A1';
    
    // Map colors to ATM card variants with project theme
    if (color.includes('#10B981') || color.includes('#059669') || color.includes('#FF89A1')) return 'atmCardGreen';
    if (color.includes('#3B82F6') || color.includes('#1D4ED8')) return 'atmCardBlue';
    if (color.includes('#8B5CF6') || color.includes('#7C3AED')) return 'atmCardPurple';
    if (color.includes('#F59E0B') || color.includes('#D97706')) return 'atmCardOrange';
    if (color.includes('#EC4899') || color.includes('#DB2777')) return 'atmCardPink';
    if (color.includes('#06B6D4') || color.includes('#0891B2')) return 'atmCardCyan';
    
    return 'atmCardGreen'; // default to project theme
  };

  return (
    <Drawer
      title="Chọn ví"
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      height="85vh"
      className={`${styles.drawer} ${styles.walletSelectionModal}`}
      bodyStyle={{ 
        padding: 0,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent'
      }}
    >
               {loading ? (
           <div style={{ textAlign: 'center', padding: '40px 20px' }}>
             <Spin size="large" />
             <div style={{ marginTop: '16px', color: '#666' }}>
               Đang tải danh sách ví...
             </div>
           </div>
         ) : filteredWallets.length === 0 ? (
           <Empty
             image={Empty.PRESENTED_IMAGE_SIMPLE}
             description="Chưa có ví nào"
             style={{ padding: '40px 20px' }}
           />
         ) : (
           <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '16px 12px' }}>
             {filteredWallets.map((wallet) => (
               <div
                 key={wallet.id}
                 className={`${styles.atmCardCompact} ${styles[getAtmCardColorClass(wallet)]} ${selectedWalletId === wallet.id ? styles.selected : ''}`}
                 onClick={() => handleWalletSelect(wallet)}
               >
                 <div className={styles.atmCardCompactHeader}>
                   <div className={styles.atmCardCompactLogo}>
                     {wallet.source_icon || <WalletOutlined />}
                   </div>
                   <div className={styles.atmCardCompactType}>
                     {wallet.source_name}
                   </div>
                 </div>
                 
                 <div className={styles.atmCardCompactName}>
                   {wallet.name}
                 </div>
                 
                 <div className={styles.atmCardCompactBalance}>
                   {getWalletBalanceDisplay(wallet)}
                 </div>
                 
                 {selectedWalletId === wallet.id && (
                   <div className={styles.atmCardCompactSelected}>
                     <div className={styles.atmCardCompactSelectedIcon}>
                       ✓
                     </div>
                   </div>
                 )}
               </div>
             ))}
           </div>
         )}
    </Drawer>
  );
};
